import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import type {
  CreatePnlMilestoneRequest,
  PatchPnlMilestoneRequest,
  PnlMilestoneDefinition,
  PnlMilestonesListResponse,
  ResetBaselineResponse,
  SuppressAllAutoMilestonesResponse,
} from "../types/milestones";

function buildUrl(path: string, params?: Record<string, string | undefined | null>): string {
  const sp = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v == null) return;
      if (String(v).trim() === "") return;
      sp.set(k, String(v));
    });
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function fetchPnlMilestones(accountCode: string): Promise<PnlMilestonesListResponse> {
  const endpoint = buildUrl("/milestones/pnl", { account_code: accountCode });
  const res = await authenticatedFetch(endpoint);
  return parseJsonOrThrow(res, "Failed to load milestones");
}

export async function createPnlMilestone(
  payload: CreatePnlMilestoneRequest
): Promise<PnlMilestoneDefinition> {
  const res = await authenticatedFetch("/milestones/pnl", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseJsonOrThrow(res, "Failed to create milestone");
}

export async function patchPnlMilestone(
  id: string,
  accountCode: string,
  patch: PatchPnlMilestoneRequest
): Promise<PnlMilestoneDefinition> {
  const endpoint = buildUrl(`/milestones/pnl/${encodeURIComponent(id)}`, { account_code: accountCode });
  const res = await authenticatedFetch(endpoint, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return parseJsonOrThrow(res, "Failed to update milestone");
}

export async function deletePnlMilestone(id: string, accountCode: string): Promise<void> {
  const endpoint = buildUrl(`/milestones/pnl/${encodeURIComponent(id)}`, { account_code: accountCode });
  const res = await authenticatedFetch(endpoint, { method: "DELETE" });
  if (res.status === 204) return;
  await parseJsonOrThrow(res, "Failed to delete milestone", { allow204: true });
}

export async function resetPnlBaseline(accountCode: string): Promise<ResetBaselineResponse> {
  const res = await authenticatedFetch("/milestones/pnl/reset-baseline", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account_code: accountCode }),
  });
  return parseJsonOrThrow(res, "Failed to reset baseline");
}

export async function suppressAllAutoPnlMilestones(
  accountCode: string,
  suppress: boolean
): Promise<SuppressAllAutoMilestonesResponse> {
  const res = await authenticatedFetch("/milestones/pnl/auto/suppress-all", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account_code: accountCode, suppress }),
  });
  return parseJsonOrThrow(res, "Failed to update AUTO milestones");
}
